import React, { useEffect, useRef, useState } from 'react';
import { ImageIcon, RefreshCw, Loader2 } from 'lucide-react';
import { api } from '../../lib/api';

/**
 * AI food photo for a meal card.
 * - First render kicks a GET /meal-image; if cached, image renders instantly.
 * - If not cached, backend queues Nano Banana generation and returns
 *   status:'generating'; we poll every 3s (up to ~2 min) until it flips
 *   to 'ready'.
 * - Long-poll uses IntersectionObserver so we don't waste API calls on
 *   off-screen meal cards.
 */
const POLL_MS = 3000;
const MAX_POLLS = 40;

const MealImage = ({ name, hint = "", className = "", aspect = "aspect-[4/3]" }) => {
  const [state, setState] = useState({ status: 'idle', url: null, err: null });
  const [visible, setVisible] = useState(false);
  const ref = useRef(null);
  const pollsRef = useRef(0);
  const timerRef = useRef(null);

  // Lazy-load: don't ask the backend until the card is on screen
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof IntersectionObserver === 'undefined') { setVisible(true); return; }
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting) { setVisible(true); io.disconnect(); }
      });
    }, { rootMargin: '200px' });
    io.observe(el);
    return () => io.disconnect();
  }, []);

  useEffect(() => {
    if (!visible || !name) return;
    let alive = true;
    pollsRef.current = 0;
    setState({ status: 'loading', url: null, err: null });

    const tick = async () => {
      if (!alive) return;
      try {
        const r = await api.getMealImage(name, hint);
        if (!alive) return;
        if (r.status === 'ready' && r.data_url) {
          setState({ status: 'ready', url: r.data_url, err: null });
          return;
        }
        if (r.status === 'failed') {
          setState({ status: 'failed', url: null, err: r.error || null });
          return;
        }
        pollsRef.current += 1;
        if (pollsRef.current >= MAX_POLLS) {
          setState({ status: 'failed', url: null, err: 'timeout' });
          return;
        }
        timerRef.current = setTimeout(tick, POLL_MS);
      } catch (err) {
        if (!alive) return;
        setState({ status: 'failed', url: null, err: err?.message || 'network' });
      }
    };
    tick();
    return () => {
      alive = false;
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [visible, name, hint]);

  const regenerate = async () => {
    setState({ status: 'loading', url: null, err: null });
    try {
      await api.regenerateMealImage(name, hint);
      pollsRef.current = 0;
      const tick = async () => {
        try {
          const r = await api.getMealImage(name, hint);
          if (r.status === 'ready' && r.data_url) { setState({ status: 'ready', url: r.data_url, err: null }); return; }
          if (r.status === 'failed') { setState({ status: 'failed', url: null, err: r.error }); return; }
          pollsRef.current += 1;
          if (pollsRef.current < MAX_POLLS) setTimeout(tick, POLL_MS);
          else setState({ status: 'failed', url: null, err: 'timeout' });
        } catch (err) { setState({ status: 'failed', url: null, err: err?.message }); }
      };
      tick();
    } catch (err) {
      setState({ status: 'failed', url: null, err: err?.message });
    }
  };

  return (
    <div
      ref={ref}
      className={`${aspect} ${className} relative w-full rounded-lg overflow-hidden bg-zinc-900 border border-zinc-800`}
      data-testid={`meal-image-${name}`}
    >
      {state.status === 'ready' && state.url ? (
        <>
          <img src={state.url} alt={name} className="w-full h-full object-cover" loading="lazy" />
          <button
            type="button"
            onClick={regenerate}
            className="absolute bottom-1.5 right-1.5 h-7 w-7 rounded-full bg-black/60 backdrop-blur text-white/80 hover:text-white flex items-center justify-center opacity-0 hover:opacity-100 focus:opacity-100 transition-opacity"
            title="Regenerate photo"
            aria-label="Regenerate photo"
          >
            <RefreshCw className="w-3 h-3" />
          </button>
        </>
      ) : state.status === 'failed' ? (
        <div className="w-full h-full flex flex-col items-center justify-center text-zinc-500 gap-1 p-2 text-center">
          <ImageIcon className="w-4 h-4" />
          <p className="text-[9px] uppercase tracking-wider">No image</p>
          <button
            type="button"
            onClick={regenerate}
            className="text-[9px] text-[#BEFF38] uppercase tracking-wider font-black hover:text-white"
          >
            Retry
          </button>
        </div>
      ) : (
        <div className="w-full h-full flex flex-col items-center justify-center text-zinc-600 gap-1.5">
          <Loader2 className="w-4 h-4 animate-spin text-[#ff7a1a]" />
          <p className="text-[9px] uppercase tracking-wider">Cooking up a photo…</p>
        </div>
      )}
    </div>
  );
};

export default MealImage;
